import { useNoaIntegrationAuthClient, useSSOAuthClient } from '@/clients';
import type { Token } from '@/shared/services/auth/models';

export interface SSOAuthClientConfig {
  redirectURI: string;
  clientId: string;
  responseType: string;
  scope: string;
  redirectDomainsWhiteList: string[];
}

export function useNoaIntegrationAuthService(options: SSOAuthClientConfig) {
  const noaIntegrationClient = useNoaIntegrationAuthClient();
  const ssoClient = useSSOAuthClient();

  return new NoaIntegrationAuthService(noaIntegrationClient, ssoClient, options);
}

class SSOAuthService {
  ssoBaseUrl: string;

  constructor(
    readonly sso: ReturnType<typeof useSSOAuthClient>,
    readonly options: SSOAuthClientConfig,
  ) {
    if (!this.sso.options.baseURL) {
      throw new Error('SSO base URL is missing');
    }

    this.ssoBaseUrl = this.sso.options.baseURL;
  }

  public redirectToLoginPage(redirectURI = this.options.redirectURI): Promise<void> {
    return new Promise(() => {
      const urlParams = new URLSearchParams({
        scope: this.options.scope,
        response_type: this.options.responseType,
        client_id: this.options.clientId,
        redirect_uri: redirectURI,
      }).toString();

      window.location.href = `${this.ssoBaseUrl}?${urlParams}`;
    });
  }

  public redirectToLogoutPage(redirectURI = this.options.redirectURI): Promise<void> {
    return new Promise(() => {
      const { origin } = new URL(this.ssoBaseUrl);

      window.location.href = `${origin}/logout?${new URLSearchParams({
        redirect_uri: redirectURI,
      }).toString()}`;
    });
  }
}

class NoaIntegrationAuthService extends SSOAuthService {
  constructor(
    readonly connect: ReturnType<typeof useNoaIntegrationAuthClient>,
    sso: ReturnType<typeof useSSOAuthClient>,
    options: SSOAuthClientConfig,
  ) {
    super(sso, options);
  }

  public exchangeForToken(code: string, redirectURI = this.options.redirectURI): Promise<Token> {
    return this.connect.fetch({
      url: '/token',
      method: 'POST',
      data: {
        code,
        redirectUrl: redirectURI,
      },
    });
  }

  public redirectToUri(redirectURI: string): Promise<void> {
    const url = new URL(redirectURI);

    if (!this.options.redirectDomainsWhiteList.includes(url.hostname)) {
      throw Error(`Redirect uri ${url.hostname} not found in the whiteList`);
    }

    return new Promise(() => {
      window.location.href = redirectURI;
    });
  }
}
